/**
 * Market recommendation popover.
 *
 * Shopify's automatic redirection only localizes a visitor's first page view,
 * and never a returning visitor who already carries a market preference. This
 * fills that gap by asking the storefront's own geo-IP endpoint,
 * `/browsing_context_suggestions.json`, what market the visitor looks like they
 * belong to, and offering — never forcing — a switch.
 *
 * Presentation (direction C, 2026-09-11): a small card anchored beneath the
 * header's country selector with a caret pointing at it. The element itself
 * lives in the layout, outside the header, because the header's utility
 * cluster is replaced wholesale after every AJAX add-to-cart
 * (PageHeader.updateFromCartChange) and would destroy a child. So the card is
 * position: fixed, and `reposition()` re-queries the anchor and measures it on
 * every call — a replaced anchor node is simply measured afresh. Where the
 * anchor is not visible (the selector is inside the mobile drawer), the card
 * hangs beneath the header bar instead.
 *
 * Accepting submits the standard `localization` form, which is a full document
 * navigation. That is load-bearing: the market-namespaced sessionStorage caches
 * in collection-grid.js and patternid-sort.js are only correct because
 * localization never changes in place.
 *
 * Request and response follow Shopify's documented localization-discovery
 * contract: the endpoint is called with `country[enabled]=true` and
 * `country[exclude]=<current>`, and answers with a `suggestions` array whose
 * entries carry `parts.country.{handle,name}`, plus a raw geo-IP
 * `detected_values.country` used as a fallback.
 *
 * That response carries NO currency field, so the currency named in the message
 * comes from Liquid (`localization.available_countries[].currency.iso_code`),
 * where it is authoritative for the market, rather than from the endpoint.
 *
 * Verified against the live store on 2026-09-10: with the US market active, a
 * US visitor localized to Australia received exactly the documented shape
 * (tests/fixtures/browsing-context-suggestions-us-20260910.json). Every read
 * below is still defensive and the card stays hidden if anything is missing.
 */
(() => {
  if (window.themeLocalizationRecommendationLoaded) return;
  window.themeLocalizationRecommendationLoaded = true;

  const SESSION_KEY = 'theme:localization-suggestion';
  const DISMISS_KEY = 'theme:localization-recommendation-dismissed';
  // Written by assets/localization-form.js on any localization submit.
  const CHOSEN_KEY = 'theme:localization-chosen';

  // Geometry. GAP is the space between the anchor's bottom edge and the card,
  // EDGE the minimum inset from the viewport's right edge, CARET_MIN/MAX keep
  // the caret on the card's top edge rather than in a rounded corner.
  const GAP = 12;
  const EDGE = 16;
  const CARET_MIN = 14;
  const CARET_HALF = 6;

  /** Storage throws in some privacy modes; never let that break the page. */
  function readStore(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* no-op */
    }
  }

  function interpolate(template, values) {
    return String(template || '').replace(/\[\[(\w+)\]\]/g, (match, key) => (
      Object.prototype.hasOwnProperty.call(values, key) && values[key] ? values[key] : match
    ));
  }

  /**
   * Where the card goes, as a pure function of rectangles so it can be proven
   * offline.
   *
   * With an anchor: the card's right edge aligns with the anchor's right edge
   * (clamped inside the viewport), and the caret points at the anchor's
   * horizontal centre, clamped onto the card's top edge.
   * Without one: below the header, inset from the right edge, caret near the
   * corner.
   *
   * @param {object} geometry
   * @param {DOMRect|null} geometry.anchor - The selector button's rect, or null.
   * @param {number} geometry.headerBottom - The header's bottom edge in viewport px.
   * @param {number} geometry.viewportWidth
   * @param {number} geometry.cardWidth - The card's rendered width.
   * @returns {{top: number, right: number, caret: number}} CSS px values.
   */
  function computePosition(geometry) {
    const viewportWidth = geometry.viewportWidth;
    const cardWidth = geometry.cardWidth;
    const anchor = geometry.anchor;

    if (!anchor || !(anchor.width > 0)) {
      return { top: Math.max(0, geometry.headerBottom) + GAP, right: EDGE, caret: 26 };
    }

    const top = anchor.bottom + GAP;

    // Right inset that aligns the card's right edge with the anchor's, kept
    // inside the viewport on both sides.
    const wanted = viewportWidth - anchor.right;
    const maxRight = Math.max(EDGE, viewportWidth - cardWidth - EDGE);
    const right = Math.min(Math.max(wanted, EDGE), maxRight);

    // Caret offset from the card's right edge, pointing at the anchor centre.
    const cardRightEdge = viewportWidth - right;
    const anchorCentre = anchor.left + anchor.width / 2;
    const caretMax = Math.max(CARET_MIN, cardWidth - CARET_MIN - 2 * CARET_HALF);
    const caret = Math.min(Math.max(cardRightEdge - anchorCentre - CARET_HALF, CARET_MIN), caretMax);

    return { top, right, caret };
  }

  class LocalizationRecommendation extends HTMLElement {
    static computePosition(geometry) {
      return computePosition(geometry);
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.form = this.querySelector('form.localization');
      this.countryInput = this.querySelector('input[name="country_code"]');
      this.messageEl = this.querySelector('[data-message]');
      this.acceptEl = this.querySelector('[data-accept]');
      if (!this.form || !this.countryInput || !this.messageEl || !this.acceptEl) return;

      this.currentCountry = (this.dataset.currentCountry || '').toUpperCase();

      // "AU:AUD,US:USD" - each served country with its presentment currency.
      this.available = [];
      this.currencies = {};
      (this.dataset.availableCountries || '').split(',').forEach((entry) => {
        const [code, currency] = entry.trim().toUpperCase().split(':');
        if (!code) return;
        this.available.push(code);
        if (currency) this.currencies[code] = currency;
      });

      if (this.available.length < 2) return;

      // Both the corner cross and the "Stay in AUD" text control dismiss.
      const dismissers = typeof this.querySelectorAll === 'function'
        ? Array.from(this.querySelectorAll('[data-dismiss]'))
        : [this.querySelector('[data-dismiss]')].filter(Boolean);
      dismissers.forEach((el) => el.addEventListener('click', this.onDismiss.bind(this)));

      this.resolve();
    }

    async resolve() {
      let suggestion = null;

      const cached = readStore('sessionStorage', SESSION_KEY);
      if (cached) {
        try {
          suggestion = JSON.parse(cached);
        } catch (e) {
          suggestion = null;
        }
      }

      if (!suggestion) {
        suggestion = await this.fetchSuggestion();
        if (suggestion) writeStore('sessionStorage', SESSION_KEY, JSON.stringify(suggestion));
      }

      if (suggestion) this.present(suggestion);
    }

    async fetchSuggestion() {
      const endpoint = this.dataset.endpoint;
      if (!endpoint) return null;

      // country[enabled] asks for country suggestions; country[exclude] keeps
      // the market the buyer is already in out of the answer. Both are part of
      // Shopify's documented request for this endpoint.
      const url = `${endpoint}?country[enabled]=true&country[exclude]=${encodeURIComponent(this.currentCountry)}`;

      try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const data = await response.json();

        // `suggestions` is the endpoint's actual recommendation and already
        // honours the exclusion; `detected_values` is raw geo-IP, used only if
        // no suggestion came back.
        const first = Array.isArray(data.suggestions) ? data.suggestions[0] : null;
        const suggested = first && first.parts && first.parts.country;
        const detected = data.detected_values && data.detected_values.country;
        const country = (suggested && suggested.handle) ? suggested : detected;
        if (!country || !country.handle) return null;

        return {
          country: String(country.handle).toUpperCase(),
          countryName: (country.name || '').toString()
        };
      } catch (e) {
        return null;
      }
    }

    present(suggestion) {
      const country = suggestion.country;

      // Only ever recommend a market the store actually serves. The endpoint
      // reports detected geography regardless of whether a market covers it,
      // and posting an unserved country_code is a silent no-op.
      if (!country || country === this.currentCountry) return;
      if (this.available.indexOf(country) === -1) return;

      if (readStore('localStorage', DISMISS_KEY) === `${this.currentCountry}>${country}`) return;

      // Anyone who has used the selector has already expressed a preference -
      // including someone who accepted this card and then switched back.
      if (readStore('localStorage', CHOSEN_KEY)) return;

      const currency = this.currencies[country] || '';
      const values = { country: suggestion.countryName || country, currency };

      // Fall back to the currency-free wording when no currency is known for
      // the market, rather than rendering a message with a hole in it.
      const message = currency ? this.dataset.message : this.dataset.messagePlain;
      const accept = currency ? this.dataset.accept : this.dataset.acceptPlain;

      this.messageEl.textContent = interpolate(message, values);
      this.acceptEl.textContent = interpolate(accept, values);
      this.countryInput.value = country;
      this.suggestedCountry = country;

      this.hidden = false;
      this.track();
    }

    /**
     * Measures the anchor and header and writes the three position custom
     * properties. Safe to call when nothing is measurable (test doubles, a
     * header that has not rendered yet): it simply leaves the CSS defaults.
     */
    reposition() {
      if (this.hidden) return;
      if (typeof document.querySelector !== 'function' || !this.style || typeof this.style.setProperty !== 'function') return;

      const anchorSelector = this.dataset.anchor || '.header-localization .custom-select__btn';
      const headerSelector = this.dataset.header || '.pageheader';

      let anchor = null;
      const anchorEl = document.querySelector(anchorSelector);
      if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
        const rect = anchorEl.getBoundingClientRect();
        // display:none (the desktop-only wrapper below the breakpoint) measures 0x0.
        if (rect.width > 0 && rect.height > 0) anchor = rect;
      }

      let headerBottom = 0;
      const headerEl = document.querySelector(headerSelector);
      if (headerEl && typeof headerEl.getBoundingClientRect === 'function') {
        headerBottom = headerEl.getBoundingClientRect().bottom;
      }

      // clientWidth, not innerWidth: a fixed element's `right` is measured
      // from the layout viewport, which excludes the scrollbar; innerWidth
      // includes it and would pull the card 15px short of the anchor.
      const viewportWidth = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;

      const pos = computePosition({
        anchor,
        headerBottom,
        viewportWidth,
        cardWidth: this.offsetWidth || 320
      });

      this.style.setProperty('--lr-top', `${Math.round(pos.top)}px`);
      this.style.setProperty('--lr-right', `${Math.round(pos.right)}px`);
      this.style.setProperty('--lr-caret', `${Math.round(pos.caret)}px`);
    }

    /**
     * Keeps the card on its anchor: on resize, on scroll (the header is sticky
     * and may collapse or hide), and after the header's utility cluster is
     * swapped by an AJAX cart update - which is why the anchor is re-queried
     * on every call rather than held.
     */
    track() {
      this.reposition();
      if (this.tracking) return;
      this.tracking = true;

      if (typeof window.addEventListener !== 'function') return;

      let scheduled = false;
      const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        const run = () => { scheduled = false; this.reposition(); };
        if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
        else setTimeout(run, 16);
      };

      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('scroll', schedule, { passive: true });
      // main.js dispatches this (bubbling, on document) after every cart
      // mutation; PageHeader.updateFromCartChange replaces the anchor node in
      // the same pass.
      document.addEventListener('on:cart:change', schedule);
      // Fonts arriving after first paint change the card's width.
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(schedule);
      }
    }

    onDismiss() {
      this.hidden = true;
      writeStore('localStorage', DISMISS_KEY, `${this.currentCountry}>${this.suggestedCountry || ''}`);
    }
  }

  if (!customElements.get('localization-recommendation')) {
    customElements.define('localization-recommendation', LocalizationRecommendation);
  }
})();
